import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { ProgressCardVM } from '../../results-vm';
import { TREND_FIRST_SIGN, TrendStatus } from '../trend-status';

interface ChartNode {
  cx: number;
  cy: number;
  r: number;
  labelY: number;
  value: number;
  dateLabel: string;
  last: boolean;
}

/**
 * wordfirst sec 4 — the trend card. The line + 4 points + values stay exactly as
 * they were (Danna, 02.09). What's added: a status WORD as an 18px title with a
 * colour dot (from the sec-5 trend rules), a single neutral "מול נקודת הפתיחה ·
 * <date>" reference chip, and one "vs start" chip whose WORD (not an arrow) states
 * the direction. Dropped: the "מול הבדיקה הקודמת" chip (sub-threshold delta + wrong
 * anchor). Date labels now live INSIDE the SVG on the points' own x (prod had them
 * in a separate space-between div = two coordinate systems that never aligned).
 *
 * This is display only — it MUST NOT bump engine_version (would reset every series).
 */
@Component({
  selector: 'app-progress-card',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './progress-card.component.html',
})
export class ProgressCardComponent implements OnChanges {
  @Input() vm: ProgressCardVM | null = null;

  // Bubble up to results.component so the locked-graph CTA opens the SAME
  // paywall-sheet MODAL (with a clear close button) as the rest of the results
  // page — consistent popup, not a page navigation (Danna, handoff 0705c #4b).
  @Output() goPaywall = new EventEmitter<void>();

  openPaywall(): void {
    this.goPaywall.emit();
  }

  nodes: ChartNode[] = [];
  linePath = '';
  areaPath = '';
  baselineY = 0;

  // trend status (sec 5)
  status: TrendStatus | null = null;
  statusWordKey = '';
  statusDotColor = '';
  /** whole-series magnitude vs the fixed first scan (drives the vs-start word). */
  vsStartMagnitude = 0;
  /** i18n key for the direction word: trend.pointsLower / trend.pointsHigher. */
  vsStartWordKey = '';

  // viewBox 0 0 320 112
  private readonly X0 = 26;
  private readonly X1 = 294;
  private readonly Y_TOP = 24; // y of the series max (with padding)
  private readonly Y_BOTTOM = 78; // y of the series min (room for date labels below)
  private readonly Y_FLOOR = 90; // area fill bottom
  private readonly Y_DATE = 106; // date caption row, inside the SVG

  ngOnChanges(): void {
    this.compute();
  }

  private compute(): void {
    const vm = this.vm;
    if (!vm || !vm.points || vm.points.length < 2) {
      this.nodes = [];
      this.status = null;
      return;
    }
    const pts = vm.points;
    const n = pts.length;
    const values = pts.map((p) => p.value);
    const min = Math.min(...values);
    const max = Math.max(...values);

    /**
     * Danna 15.09, "the graph does not look steady". The y axis was scaled to
     * the series range alone, so the two points ALWAYS filled the full height
     * and every difference looked identical: the 4-point change in her
     * screenshot, which this very card declares "Steady" and explains as "we
     * flag a change of 5 points or more", was drawn as a dramatic full-height
     * climb. The picture contradicted the verdict printed above it.
     *
     * The domain now never narrows below the whole steady band, plus or minus
     * the flag threshold the card already publishes. So the slope is
     * proportional to the product's own numbers: under 5 points reads as the
     * gentle drift it is, 10 or more still fills the chart.
     */
    // Danna 13.10: a 9-point same-day move still looked too dramatic. Keep at
    // least 40 points of range so a single-scan swing reads as a gentle slope.
    const minDomain = Math.max(TREND_FIRST_SIGN * 2, 40);
    const mid = (min + max) / 2;
    const half = Math.max((max - min) / 2, minDomain / 2);
    const lo = mid - half;
    const span = half * 2;

    const xOf = (i: number) => this.X0 + (this.X1 - this.X0) * (i / (n - 1));
    // For a lower-is-better metric (vm.invert) plot the value inverted so that
    // "better" is always UP — an improving score (a drop) then rises on the
    // chart. Raw numbers are still shown as the point labels.
    const yOf = (v: number) => {
      if (span <= 0) return (this.Y_TOP + this.Y_BOTTOM) / 2;
      const frac = (v - lo) / span; // 0 at the domain floor, 1 at its ceiling
      const f = vm.invert ? 1 - frac : frac;
      return this.Y_BOTTOM - f * (this.Y_BOTTOM - this.Y_TOP);
    };

    this.nodes = pts.map((p, i) => {
      const cy = yOf(p.value);
      const last = i === n - 1;
      return {
        cx: xOf(i),
        cy,
        r: last ? 4.5 : 3,
        // never let a high point push its own value label off the top edge
        labelY: Math.max(cy - 9, 11),
        value: p.value,
        dateLabel: this.fmtDM(p.date),
        last,
      };
    });

    this.baselineY = this.nodes[0].cy;
    let line = `M${this.nodes[0].cx.toFixed(1)} ${this.nodes[0].cy.toFixed(1)}`;
    for (let i = 1; i < this.nodes.length; i++) {
      line += ` L${this.nodes[i].cx.toFixed(1)} ${this.nodes[i].cy.toFixed(1)}`;
    }
    this.linePath = line;
    this.areaPath = `${line} L${this.nodes[n - 1].cx.toFixed(1)} ${this.Y_FLOOR} L${this.nodes[0].cx.toFixed(1)} ${this.Y_FLOOR} Z`;

    // ---- trend status (sec 5): fixed FIRST-scan reference, thresholds 5/10 ----
    // Read the status from the VM, where it is computed on the FULL uncapped
    // series (results-vm.service.buildProgress). vm.points is capped to the last
    // 4 for the PLOT only, so computing here would anchor the word on the
    // 4th-from-last scan for 5+ scans — a rolling reference (validator bug).
    this.status = vm.trendStatus;
    this.statusWordKey = `trend.states.${this.status.state}`;
    this.statusDotColor =
      this.status.color === 'green'
        ? 'var(--status-green)'
        : this.status.color === 'amber'
          ? 'var(--status-amber)'
          : 'var(--st-blue)';
    this.vsStartMagnitude = this.status.magnitude;
    this.vsStartWordKey =
      this.status.rawDir < 0 ? 'trend.pointsLower' : 'trend.pointsHigher';
  }

  readonly dateY = this.Y_DATE;

  private fmtDM(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return `${d.getDate()}.${d.getMonth() + 1}`;
  }
}
